'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

// Context to share hover state between Tooltip and TooltipTrigger
const TooltipHoverContext = React.createContext<{
  setIsHovered: (hovered: boolean) => void;
  /** A press drops a still-pending delayed open. */
  cancelPending: () => void;
} | null>(null);

function Tooltip({
  delayDuration = 0,
  disableHoverableContent = true,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const [open, setOpen] = React.useState(false);
  const hoveredRef = React.useRef(false);
  const timerRef = React.useRef<number | undefined>(undefined);

  // Hover opens it (after `delayDuration` ms when set); leaving closes it.
  const setIsHovered = React.useCallback(
    (hovered: boolean) => {
      hoveredRef.current = hovered;
      window.clearTimeout(timerRef.current);
      if (!hovered) setOpen(false);
      else if (delayDuration <= 0) setOpen(true);
      else
        timerRef.current = window.setTimeout(
          () => setOpen(true),
          delayDuration
        );
    },
    [delayDuration]
  );
  React.useEffect(() => () => window.clearTimeout(timerRef.current), []);
  const hoverContext = React.useMemo(
    () => ({
      setIsHovered,
      cancelPending: () => window.clearTimeout(timerRef.current),
    }),
    [setIsHovered]
  );

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <TooltipHoverContext.Provider value={hoverContext}>
        <TooltipPrimitive.Root
          data-slot="tooltip"
          disableHoverableContent={disableHoverableContent}
          open={open}
          onOpenChange={(newOpen) => {
            // Only hover opens it (never keyboard focus); a delayed tooltip
            // opens from the timer above, not from Radix.
            if (!newOpen) setOpen(false);
            else if (hoveredRef.current && delayDuration <= 0) setOpen(true);
          }}
          {...props}
        />
      </TooltipHoverContext.Provider>
    </TooltipProvider>
  );
}

function TooltipTrigger({
  onFocus,
  onPointerEnter,
  onPointerLeave,
  onPointerDown,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  const context = React.useContext(TooltipHoverContext);

  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      onFocus={(e) => {
        // Don't show tooltip on keyboard focus - only on hover
        // Allow focus for accessibility but prevent tooltip from opening
        onFocus?.(e);
      }}
      onPointerEnter={(e) => {
        // Mark as hovered so tooltip can open
        context?.setIsHovered(true);
        onPointerEnter?.(e);
      }}
      onPointerLeave={(e) => {
        // Mark as not hovered
        context?.setIsHovered(false);
        onPointerLeave?.(e);
      }}
      onPointerDown={(e) => {
        context?.cancelPending();
        onPointerDown?.(e);
      }}
      {...props}
    />
  );
}

type TooltipContentProps = React.ComponentProps<
  typeof TooltipPrimitive.Content
> & {
  hideArrow?: boolean;
};

/**
 * Tooltips are labels, never interactive surfaces. The content (and, via
 * src/styles/tooltip.css, its Radix popper wrapper) must not take pointer
 * hits: the entry animation slides the bubble up to 8px toward its trigger,
 * so a pointer resting near the trigger's border would land on the bubble,
 * fire pointerleave on the trigger, close the tooltip, land back on the
 * trigger and reopen it — the open/close flicker loop.
 */
export const TOOLTIP_CONTENT_HIT_TEST_CLASS = 'pointer-events-none select-none';
/** Keeps flipped/shifted bubbles off the viewport edge. */
export const TOOLTIP_COLLISION_PADDING = 8;
/** Gap used when no arrow supplies its own spacing. */
export const TOOLTIP_ARROWLESS_SIDE_OFFSET = 4;

function TooltipContent({
  className,
  hideArrow = false,
  sideOffset = hideArrow ? TOOLTIP_ARROWLESS_SIDE_OFFSET : 0,
  collisionPadding = TOOLTIP_COLLISION_PADDING,
  children,
  ...props
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          TOOLTIP_CONTENT_HIT_TEST_CLASS,
          'bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[10000] w-fit origin-(--radix-tooltip-content-transform-origin) rounded-lg px-3 py-1.5 text-xs text-balance',
          className
        )}
        {...props}
      >
        {children}
        {hideArrow ? null : (
          <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-[10000] size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
